import { ApiResponseUnion } from "app/shared/models/api-response.model";

/**
 * Utilitário para tratamento de erros da API
 */
export class ErrorApiUtils {
    /**
     * Extrai e concatena mensagens de erro de uma resposta da API
     * @param error Resposta de erro da API ou erro genérico
     * @param defaultMessage Mensagem padrão caso não encontre erros específicos
     * @returns Mensagem de erro formatada
     */
    static extractErrorMessage(
        error: any,
        defaultMessage: string = "Erro inesperado"
    ): string {
        // Verifica se é um erro de resposta HTTP
        if (error?.error) {
            const errorBody = error.error;

            // Se tem array de errors, concatena as mensagens
            if (
                errorBody.errors &&
                Array.isArray(errorBody.errors) &&
                errorBody.errors.length > 0
            ) {
                const errorMessages = errorBody.errors
                    .map((errorObj: Record<string, string>) =>
                        Object.values(errorObj)
                    )
                    .flat()
                    .filter(
                        (message: string) => message && message.trim() !== ""
                    )
                    .join(". ");

                if (errorMessages) {
                    return this.sanitizeErrorMessage(errorMessages);
                }
            }

            // Se tem message na resposta, usa ela
            if (errorBody.message && typeof errorBody.message === "string") {
                return this.sanitizeErrorMessage(errorBody.message);
            }
        }

        // Fallback para mensagem do erro genérico
        if (error?.message && typeof error.message === "string") {
            return this.sanitizeErrorMessage(error.message);
        }

        // Retorna a mensagem padrão
        return defaultMessage;
    }

    /**
     * Verifica se a resposta da API contém erros
     * @param response Resposta da API
     * @returns true se contém erros, false caso contrário
     */
    static hasApiErrors(response: ApiResponseUnion<any>): boolean {
        return (
            response.success === false ||
            !!(
                response.errors &&
                Array.isArray(response.errors) &&
                response.errors.length > 0
            )
        );
    }

    /**
     * Sanitiza uma mensagem de erro para exibição
     * @param message Mensagem original
     * @returns Mensagem sanitizada
     */
    static sanitizeErrorMessage(message: string): string {
        if (!message || typeof message !== "string" || message.trim() === "") {
            return "Erro inesperado";
        }

        // Remove caracteres especiais e normaliza
        return message
            .trim()
            .replace(/\.$/, "") // Remove ponto final se existir
            .concat("."); // Adiciona ponto final
    }
}
